import * as React from "react";
import { useEffect } from "react";
import { Box, Typography, Button, useMediaQuery, useTheme, Grid, Skeleton, Snackbar, Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { centered_flex_box, main_button } from "../../app/components/Styles";
import { PasswordVerificationModal } from "../../app/components";
import { refreshPassword } from "../../app/store/actions/authActions";
import { returnsSystemUnsubscribe, getReturnsSystemDashboard } from "../../app/store/actions/dataActions";
import NewCard from "../../app/components/NewCard";
import Ellipse from "../../app/components/Ellipse";
import { CopyAll, Tune, Inventory2, Autorenew } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ReturnsSystemHome = ({ user, refreshPassword, returnsSystemUnsubscribe, getReturnsSystemDashboard, dashboard, isLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [passwordModalOpen, setPasswordModalOpen] = React.useState(false);
  const [snackBar, setSnackBar] = React.useState(false);

  useEffect(() => {
    if (!user?.vendor?.hasReturnSystem) {
      navigate("/returns-system/subscribe", { replace: true });
    } else {
      getReturnsSystemDashboard({});
    }
  }, [user]);

  if (!user?.vendor?.hasReturnSystem) {
    return null;
  }

  const openUnsubscribe = () => setPasswordModalOpen(true);
  const closeModal = () => setPasswordModalOpen(false);

  const handleVerify = async (password) => {
    try {
      const result = await refreshPassword(password);
      if (result.type === "WAIT_SUCCESS") {
        setPasswordModalOpen(false);
        returnsSystemUnsubscribe(undefined, () => {});
      }
    } catch (e) {}
  };

  const pendingReturns = dashboard?.pendingReturnRequests || 0;
  const pendingExchanges = dashboard?.pendingExchangeRequests || 0;
  const portalUrl = `https://returns.chainzsolutions.com/returns/${user?._id}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setSnackBar(true);
    } catch {}
  };

  return (
    <Box sx={{ width: "100%", paddingX: isMobile ? 2 : 5 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", gap: 1, mb: 1 }}>
          <Box>
            <Typography sx={{ color: "var(--secColor4)", fontWeight: 700 }} fontSize={isMobile ? 22 : 28}>Backtrack</Typography>
            <Typography sx={{ color: "var(--secColor4)", opacity: 0.8, fontSize: isMobile ? 14 : 16 }}>{t("returnsSystem.home.tagline")}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button sx={{ ...main_button }} onClick={() => navigate("/returns-system/subscribe")} startIcon={<Tune sx={{ color: "var(--secColor4)" }} />}>{t("returnsSystem.home.customize")}</Button>
            <Button sx={{ ...main_button }} onClick={() => navigate("/returns-system/manage-products")} startIcon={<Inventory2 sx={{ color: "var(--secColor4)" }} />}>{t("returnsSystem.home.manageProducts")}</Button>
            <Button sx={{ ...main_button, bgcolor: 'var(--errorColor)' }} onClick={openUnsubscribe}>{t("returnsSystem.home.unsubscribe")}</Button>
          </Box>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <NewCard sx={{ minHeight: 250 }} content={
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Typography fontWeight={"bold"} marginBottom={2}>
                {t("returnsSystem.home.pendingReturnRequests")}
              </Typography>
              <Box sx={{ ...centered_flex_box, flexDirection: "column", flex: 1, mb: 2 }}>
                {!isLoading && <Ellipse size={120} text={pendingReturns} />}
                {isLoading && <Skeleton variant="circular" animation="wave" width={120} height={120} />}
              </Box>
              <Button 
                sx={{ ...main_button, width: '100%' }} 
                onClick={() => navigate("/returns-system/return-requests")}
              >
                {t("returnsSystem.home.returnRequestsCta")}
              </Button>
            </Box>
          } />
        </Grid>
        <Grid item xs={12} md={6}>
          <NewCard sx={{ minHeight: 250 }} content={
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
              <Typography fontWeight={"bold"} marginBottom={2}>
                {t("returnsSystem.home.pendingExchangeRequests")}
              </Typography>
              <Box sx={{ ...centered_flex_box, flexDirection: "column", flex: 1, mb: 2 }}>
                {!isLoading && <Ellipse size={120} text={pendingExchanges} />} 
                {isLoading && <Skeleton variant="circular" animation="wave" width={120} height={120} />}
              </Box>
              <Button 
                sx={{ ...main_button, width: '100%' }} 
                onClick={() => navigate("/returns-system/exchange-requests")}
              >
                {t("returnsSystem.home.exchangeRequestsCta")}
              </Button>
            </Box>
          } />
        </Grid>
      </Grid>

      {/* Portal URL */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12}>
          <NewCard content={
            <Box>
              <Typography variant="h6" fontWeight={700}>{t("returnsSystem.home.publicPortalUrlTitle")}</Typography>
              <Typography gutterBottom={15} sx={{ color: 'var(--secColor4)' }}>{t("returnsSystem.home.publicPortalUrlDescription")}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ color: 'var(--primaryColor)', textDecoration: 'underline' }}>{portalUrl}</Typography>
                <Tooltip title={t("returnsSystem.home.copyUrl")}><IconButton sx={{ color: "var(--secColor4)" }} onClick={copyUrl}><CopyAll /></IconButton></Tooltip>
              </Box>
            </Box>
          } />
        </Grid>
      </Grid>

      {/* Shopify Steps */}
      <NewCard content={
        <Box>
          <Typography variant="h6" fontWeight={700}>{t("returnsSystem.home.addToShopifyNavigationTitle")}</Typography>
          <Typography gutterBottom={15}>{t("returnsSystem.home.shopifySteps.step1")}</Typography>
          <Typography gutterBottom={15}>{t("returnsSystem.home.shopifySteps.step2")} <strong>{t("returnsSystem.home.shopifySteps.step2Bold")}</strong>.</Typography>
          <Typography gutterBottom={15}>{t("returnsSystem.home.shopifySteps.step3")}</Typography>
          <Typography gutterBottom={15}>{t("returnsSystem.home.shopifySteps.step4")} <strong>{t("returnsSystem.home.shopifySteps.step4Bold")}</strong>.</Typography>
          <Typography gutterBottom={15}>{t("returnsSystem.home.shopifySteps.step5Prefix")} <strong>{t("returnsSystem.home.shopifySteps.step5TitleBold")}</strong> {t("returnsSystem.home.shopifySteps.step5Mid")} <strong>{t("returnsSystem.home.shopifySteps.step5LinkBold")}</strong> {t("returnsSystem.home.shopifySteps.step5Suffix")} <strong>{t("common.save")}</strong>. <Button onClick={copyUrl} sx={{ ...main_button, px: 1, py: 0, marginInlineStart: 1 }}>{t("returnsSystem.home.copyUrl")}</Button></Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'var(--warningColor)', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 1 }}>
              {t("returnsSystem.home.pricingStructureTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
              {t("returnsSystem.home.pricingStructure.base")}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
              {t("returnsSystem.home.pricingStructure.tier1")}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
              {t("returnsSystem.home.pricingStructure.tier2")}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {t("returnsSystem.home.pricingStructure.cancelAnytime")}
            </Typography>
          </Box>
        </Box>
      } />

      <PasswordVerificationModal
        open={passwordModalOpen}
        onClose={closeModal}
        onVerify={handleVerify}
        isLoading={isLoading}
        title={t("returnsSystem.home.verifyPasswordTitle")}
        message={t("returnsSystem.home.verifyPasswordMessage")}
      />

      <Snackbar
        open={snackBar}
        autoHideDuration={3000}
        onClose={() => setSnackBar(false)}
        message={t("returnsSystem.home.urlCopied")}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  dashboard: state?.records?.returnsSystemDashboard,
});

const mapDispatchToProps = { refreshPassword, returnsSystemUnsubscribe, getReturnsSystemDashboard };

export default connect(mapStateToProps, mapDispatchToProps)(ReturnsSystemHome);

